import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { getDebtDetail } from '../../../services/debt';
import { KEY, loadState, saveState } from './state';

export const useDebtorSelection = ({ id, navigate, setPreviewIndex }) => {
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [selectedDebtorKeys, setSelectedDebtorKeys] = useState([]);
  const [debtorViewType, setDebtorViewType] = useState('P');
  const initializedViewTypeRef = useRef(false);

  const personalRows = useMemo(() => {
    const list = Array.isArray(selectedDebt?.debtorsPersonal) ? selectedDebt.debtorsPersonal : [];
    return list.map((d, idx) => ({
      key: d?.id ?? `P-${idx}`,
      type: 'P',
      name: d?.name || '',
      idNo: d?.idNo || '',
      phone: d?.phone || '',
      email: d?.email || '',
    }));
  }, [selectedDebt]);

  const enterpriseRows = useMemo(() => {
    const list = Array.isArray(selectedDebt?.debtorsEnterprise) ? selectedDebt.debtorsEnterprise : [];
    return list.map((d, idx) => ({
      key: d?.id ?? `E-${idx}`,
      type: 'E',
      companyName: d?.companyName || '',
      socialCreditCode: d?.socialCreditCode || '',
      legalPersonName: d?.legalPersonName || '',
      legalPersonIdNo: d?.legalPersonIdNo || '',
      phone: d?.phone || '',
      email: d?.email || '',
    }));
  }, [selectedDebt]);

  const allRows = useMemo(() => [...personalRows, ...enterpriseRows], [personalRows, enterpriseRows]);

  const rowByKey = useMemo(() => {
    const m = {};
    allRows.forEach((r) => { m[r.key] = r; });
    return m;
  }, [allRows]);

  const normalizeDebtorKeys = (keys, preferredType) => {
    const raw = Array.isArray(keys) ? keys : [];
    const dedup = Array.from(new Set(raw));
    const existing = dedup.filter((k) => rowByKey?.[k]);
    const types = new Set();
    existing.forEach((k) => {
      const t = rowByKey?.[k]?.type;
      if (t === 'P' || t === 'E') types.add(t);
    });
    if (types.size <= 1) return existing;
    const targetType = types.has(preferredType) ? preferredType : rowByKey?.[existing[0]]?.type;
    return existing.filter((k) => rowByKey?.[k]?.type === targetType);
  };

  useEffect(() => {
    const state = loadState();
    if (!state.selectedDebtId && !id) {
      navigate('/debt-notice/publish');
      return;
    }
    const debtId = id || state.selectedDebtId;
    getDebtDetail(debtId).then(({ data }) => setSelectedDebt(data)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    const state = loadState();
    sessionStorage.setItem(KEY, JSON.stringify({ ...state, saved: false }));
  }, []);

  useEffect(() => {
    const state = loadState();
    const savedKeys = Array.isArray(state.selectedDebtorKeys) ? state.selectedDebtorKeys : null;
    const allKeys = [...personalRows.map(r => r.key), ...enterpriseRows.map(r => r.key)];
    const keys = savedKeys && savedKeys.length > 0 ? savedKeys : allKeys;
    const normalizedKeys = normalizeDebtorKeys(keys, rowByKey?.[keys?.[0]]?.type);
    setSelectedDebtorKeys(normalizedKeys);
    const selected = [...personalRows, ...enterpriseRows].filter(r => normalizedKeys.includes(r.key));
    saveState({ selectedDebtorKeys: normalizedKeys, selectedDebtors: selected });
    setPreviewIndex?.(0);

    if (!initializedViewTypeRef.current) {
      const t = rowByKey?.[normalizedKeys?.[0]]?.type;
      if (t === 'P' || t === 'E') setDebtorViewType(t);
      initializedViewTypeRef.current = true;
    }
  }, [enterpriseRows, personalRows, rowByKey, setPreviewIndex]);

  const applySelectedDebtors = (nextKeys, preferredType, silent = false) => {
    const normalizedKeys = normalizeDebtorKeys(nextKeys, preferredType);
    if (!silent) {
      const types = new Set();
      (nextKeys || []).forEach((k) => {
        const t = rowByKey?.[k]?.type;
        if (t === 'P' || t === 'E') types.add(t);
      });
      if (types.size > 1) {
        message.warning('不允许同时勾选个人债务人和企业债务人，已自动取消另一类');
      }
    }
    setSelectedDebtorKeys(normalizedKeys);
    const selected = allRows.filter(r => normalizedKeys.includes(r.key));
    setPreviewIndex?.(0);
    saveState({ selectedDebtorKeys: normalizedKeys, selectedDebtors: selected, activeDebtorKey: normalizedKeys[0] || null });
  };

  const selectedDebtorTypes = useMemo(() => {
    const set = new Set();
    (selectedDebtorKeys || []).forEach((k) => {
      const t = rowByKey?.[k]?.type;
      if (t === 'P' || t === 'E') set.add(t);
    });
    return set;
  }, [rowByKey, selectedDebtorKeys]);

  const creditorType = String(selectedDebt?.creditor?.creditorType || '').toUpperCase();
  const showCreditorPersonal = creditorType ? creditorType === 'P' : true;
  const showCreditorEnterprise = creditorType ? creditorType === 'E' : true;

  const showDebtorPersonal = selectedDebtorTypes.size === 0 ? true : selectedDebtorTypes.has('P');
  const showDebtorEnterprise = selectedDebtorTypes.size === 0 ? true : selectedDebtorTypes.has('E');

  const debtorTypeOptions = useMemo(() => {
    const list = [];
    if (personalRows.length > 0) list.push({ label: `个人债务人（${personalRows.length}）`, value: 'P' });
    if (enterpriseRows.length > 0) list.push({ label: `企业债务人（${enterpriseRows.length}）`, value: 'E' });
    return list;
  }, [enterpriseRows, personalRows]);

  useEffect(() => {
    if (!debtorTypeOptions.length) return;
    const currentAvailable = debtorTypeOptions.some((o) => o.value === debtorViewType);
    if (!currentAvailable) {
      setDebtorViewType(debtorTypeOptions[0].value);
    }
  }, [debtorTypeOptions, debtorViewType]);

  const currentDebtorRows = useMemo(() => (debtorViewType === 'E' ? enterpriseRows : personalRows), [debtorViewType, enterpriseRows, personalRows]);

  return {
    selectedDebt,
    personalRows,
    enterpriseRows,
    allRows,
    rowByKey,
    selectedDebtorKeys,
    setSelectedDebtorKeys,
    debtorViewType,
    setDebtorViewType,
    currentDebtorRows,
    debtorTypeOptions,
    applySelectedDebtors,
    showCreditorPersonal,
    showCreditorEnterprise,
    showDebtorPersonal,
    showDebtorEnterprise,
  };
};
